import api from "../components/login/interceptors";
import { BASE_URL } from "../Constants";

export const panelistsUrl = `${BASE_URL}/api/interview-panelists`;

export const MAX_PANELIST_SKILLS = 50;

export const panelistIdOf = (person) => person?.panelist_id || person?.interviewer_id || "";

const NAME_MAX = 200;

// `name` is the first name. `last_name` is a separate optional field.
export const panelistNameParts = (person = {}) => ({
  firstName: String(person?.name || "").trim(),
  lastName: String(person?.last_name || "").trim(),
});

export const panelistDisplayName = (person) => {
  const { firstName, lastName } = panelistNameParts(person);
  return [firstName, lastName].filter(Boolean).join(" ");
};

export const panelistNamePayload = ({ name = "", lastName = "" } = {}) => ({
  name: String(name || "").trim(),
  last_name: String(lastName || "").trim(),
});

export const panelistNameError = ({ firstName = "", lastName = "" } = {}) => {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  if (!first) return "First name is required";
  if (first.length > NAME_MAX) return "First name must be 200 characters or less.";
  if (last.length > NAME_MAX) return "Last name must be 200 characters or less.";
  return "";
};

export const normalizeSkills = (skills, draft = "") => {
  const list = (Array.isArray(skills) ? skills : [])
    .map((skill) => String(skill || "").trim())
    .filter(Boolean);
  const pending = String(draft || "").trim();
  if (pending && !list.some((skill) => skill.toLowerCase() === pending.toLowerCase())) {
    list.push(pending);
  }
  const seen = new Set();
  return list.filter((skill) => {
    const key = skill.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const panelistProfilePayload = ({
  role = "",
  experience = "",
  skills = [],
  skillDraft = "",
} = {}) => {
  const years = String(experience ?? "").trim();
  const hasYears = /^\d+$/.test(years);
  return {
    role: String(role || "").trim() || null,
    skills: normalizeSkills(skills, skillDraft),
    experience: hasYears ? Number(years) : null,
    experience_plus: false,
  };
};

export const panelistFieldError = ({
  role = "",
  experience = "",
  skills = [],
  skillDraft = "",
} = {}) => {
  if (String(role || "").trim().length > 100) return "Designation must be 100 characters or less.";
  const list = normalizeSkills(skills, skillDraft);
  if (list.length > MAX_PANELIST_SKILLS) {
    return `A panelist can have up to ${MAX_PANELIST_SKILLS} skills.`;
  }
  if (list.some((skill) => skill.length > 100)) return "Each skill must be 100 characters or less.";
  const years = String(experience ?? "").trim();
  if (years !== "") {
    const count = Number(years);
    if (!/^\d+$/.test(years) || count > 60) return "Experience must be a whole number from 0 to 60.";
  }
  return "";
};

// PATCH /api/me fields for a panelist editing their own profile. experience_plus is left out so a saved
// "N+ years" is kept; the server turns it off when experience is cleared.
export const selfPanelistPayload = (profile) => {
  const { role, skills, experience } = panelistProfilePayload(profile);
  return { designation: role, skills, experience };
};

export const formatPanelistExperience = (person) => {
  const years = person?.experience;
  if (years === null || years === undefined || years === "") return "—";
  const count = Number(years);
  if (!Number.isInteger(count) || count < 0) return "";
  const plus = person?.experience_plus ? "+" : "";
  return `${count}${plus} ${count === 1 ? "year" : "years"}`;
};

export const emptyPanelistProfile = () => ({
  role: "",
  experience: "",
  skills: [],
  skillDraft: "",
});

// designation and role hold the same value; GET /me documents designation
export const profileFromPanelist = (person) => ({
  role: person?.designation || person?.role || "",
  experience:
    person?.experience === 0 || person?.experience
      ? String(person.experience)
      : "",
  skills: Array.isArray(person?.skills) ? person.skills.filter(Boolean) : [],
  skillDraft: "",
});

export const fetchPanelistPage = async ({ search = "", page = 1, limit = 50 } = {}) => {
  const response = await api.get(panelistsUrl, {
    params: { search, page, limit },
  });
  const data = response.data || {};
  const panelists = Array.isArray(data.panelists) ? data.panelists : [];
  return {
    panelists,
    count: data.count ?? panelists.length,
    page: data.page ?? page,
    limit: data.limit ?? limit,
  };
};

export const fetchAllPanelists = async () => {
  const first = await fetchPanelistPage({ page: 1, limit: 50 });
  const panelists = [...first.panelists];
  const pageSize = first.limit || 50;
  const pages = Math.ceil((first.count || panelists.length) / pageSize);
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchPanelistPage({ page, limit: pageSize });
    panelists.push(...next.panelists);
  }
  return panelists;
};

export const panelistErrorMessage = (err, fallback) => {
  const data = err?.response?.data || {};
  const code = data.code || data.error;
  if (code === "PANELIST_EMAIL_EXISTS") return "A panelist with this email already exists.";
  if (code === "PANELIST_HAS_UPCOMING_INTERVIEWS") {
    return "This panelist has upcoming interviews. Reschedule or cancel them first.";
  }
  if (code === "EMAIL_REQUIRED") return "Email is required.";
  if (code === "EMAIL_INVALID") return "Enter a valid email address.";
  if (code === "NAME_REQUIRED" || code === "FIRST_NAME_REQUIRED") return "First name is required";
  if (code === "PANELIST_NOT_FOUND") return "Panelist not found.";
  if (code === "VALIDATION_ERROR") return data.error || "Check the panelist details and try again.";
  if (err?.response?.status === 409) {
    return data.error || data.message || "This panelist has upcoming interviews and can't be deleted.";
  }
  return data.error || data.message || fallback;
};
